/** Hide skill: stationary concealment with exponential cooldown decay, danger-gated sustained XP, and danger-adjusted success rolls. */
import { ConcealmentSkillEffect } from './ConcealmentSkillEffect';
import type { ConcealmentCallbacks, ConcealmentTuning } from './ConcealmentSkillEffect';
import { ConfigLoader } from '../../systems/ConfigLoader';
import { DebugConfig } from '../../debug/DebugConfig';

export interface HideCallbacks extends ConcealmentCallbacks {
  isPlayerStationary: () => boolean;
  isStealthActive: () => boolean;
  onHideBreak: () => void;
}

/** SkillEffect for the Hide ability: stationary concealment with exponential cooldown curve. */
export class HideSkillEffect extends ConcealmentSkillEffect {
  readonly skillId = 'hide';
  declare protected callbacks: HideCallbacks;

  constructor(callbacks: HideCallbacks) {
    super(callbacks);
  }

  protected getTuning(): ConcealmentTuning {
    return ConfigLoader.hideTuning;
  }

  /** Exponential decay: steep early drop, asymptotes toward minCooldownMs. */
  get currentCooldownMs(): number {
    const config = ConfigLoader.hideTuning;
    const xp = this.callbacks.getXp();
    const range = config.maxCooldownMs - config.minCooldownMs;
    const k = config.cooldownDecayRate;
    return config.minCooldownMs + range * Math.exp(-k * xp);
  }

  protected getCooldownMs(): number {
    return this.currentCooldownMs;
  }

  protected canActivateExtra(): boolean {
    return this.callbacks.isPlayerStationary();
  }

  protected onActivateSuccess(): void {
    // No additional setup for Hide
  }

  /** Danger-gated sustained XP: timer only ticks when danger exceeds threshold. */
  protected updateSustainedXp(delta: number): void {
    const danger = this.callbacks.getDanger();
    const config = ConfigLoader.hideTuning;
    if (danger < config.dangerGateThreshold) return;

    this.sustainedXpTimer += delta;
    if (this.sustainedXpTimer >= config.sustainedXpIntervalMs) {
      this.sustainedXpTimer -= config.sustainedXpIntervalMs;
      this.callbacks.onXpGained(this.skillId, {
        enemyHasLOS: this.callbacks.anyEnemyHasLOS(),
        dangerPresent: danger > 0,
      });
    }
  }

  protected shouldBreakOnUpdate(_delta: number): boolean {
    // Movement breaks hide (unless stealth is active — stealth allows movement)
    return !this.callbacks.isPlayerStationary() && !this.callbacks.isStealthActive();
  }

  protected onBreakConceal(): void {
    if (!DebugConfig.flags.zeroCooldowns) {
      this.cooldownRemaining = this.currentCooldownMs;
    }
    this.callbacks.onHideBreak();
  }

  /** Force-activate Hide internally (called by Stealth evolution). No roll, no cooldown, no XP. */
  forceActivate(): void {
    this._isActive = true;
    this.sustainedXpTimer = 0;
  }

  /** Force-break Hide from external callers (e.g. dash path in GameScene). */
  breakHide(): void {
    this.breakConceal();
  }
}
